import React, { useState, useEffect, useCallback, useRef, useMemo, memo } from 'react';
import MaterialTable from 'material-table';
import { useSelector, useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import { api } from 'common';
import { colors } from '../components/Theme/WebTheme';
import { makeStyles } from '@mui/styles';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import TextField from '@mui/material/TextField';
import SettingsIcon from '@mui/icons-material/Settings';

const useStyles = makeStyles(theme => ({
  form: {
    width: '100%',
    marginTop: theme.spacing(1),
    backgroundColor: colors.SECONDARY,
    alignContent: 'center',
    borderRadius: "8px",
  },
  container1: {
    backgroundColor: colors.WHITE,
    borderTopLeftRadius: "0px",
    borderTopRightRadius: "0px",
    borderBottomLeftRadius: "8px",
    borderBottomRightRadius: "8px",
    padding: '30px',
    width: '100%',
    top: "19px",
    boxShadow: "4px 4px 6px #9E9E9E"
  },
  title: {
    color: colors.WHITE,
    marginBottom: '15px',
    paddingTop: '15px',
    paddingLeft: '15px',
    fontSize: '20px'
  },
}));

const PredefinedFeedbackSettings = React.memo(() => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.dir();
  const classes = useStyles();
  const dispatch = useDispatch();

  const {
    editSettings,
    clearSettingsViewError
  } = api;

  // Redux state
  const predefiniedRatingFeedbackSetting = useSelector(state => state.predefiniedRatingFeedbackSetting);
  const [data, setData] = useState([]);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [isSaving, setIsSaving] = useState(false);

  // Fetch data from Firebase on component mount
  const fetchDataFromFirebase = useCallback(async () => {
    try {
      console.log('🔄 Fetching data from Firebase...');
      const { firebase } = require('common/src/config/configureFirebase');
      const { onValue, ref } = require("firebase/database");

      const feedbackRef = ref(firebase.database, 'predefiniedRatingFeedbackSetting');

      onValue(feedbackRef, (snapshot) => {
        if (snapshot.exists()) {
          const data = snapshot.val();
          console.log('📥 Data received from Firebase:', data);

          // Convert object to array if needed
          let feedbackArray = [];
          if (Array.isArray(data)) {
            feedbackArray = data;
          } else if (typeof data === 'object') {
            feedbackArray = Object.values(data);
          }

          if (feedbackArray.length > 0) {
            const feedbackOptions = feedbackArray.map((item, index) => ({
              id: item.id || `feedback_${index}`,
              text: item.text || '',
              isActive: item.isActive !== false,
              order: item.order || (index + 1)
            }));
            const sortedOptions = feedbackOptions.sort((a, b) => a.order - b.order);
            console.log('✅ Processed feedback options from Firebase:', sortedOptions);
            setData(sortedOptions);
          } else {
            console.log('📭 No feedback data in Firebase, showing empty table');
            setData([]);
          }
        } else {
          console.log('📭 No data in Firebase, showing empty table');
          setData([]);
        }
      }, (error) => {
        console.error('❌ Error fetching data from Firebase:', error);
        setSnackbar({
          open: true,
          message: 'Error loading feedback data from database',
          severity: 'error'
        });
      });
    } catch (error) {
      console.error('❌ Error setting up Firebase listener:', error);
    }
  }, []);

  // Load initial data
  useEffect(() => {
    console.log('🔄 Loading feedback data from Redux:', predefiniedRatingFeedbackSetting);

    if (predefiniedRatingFeedbackSetting?.data) {
      // Handle both array and object formats
      let feedbackArray = [];

      if (Array.isArray(predefiniedRatingFeedbackSetting.data)) {
        // If it's already an array
        feedbackArray = predefiniedRatingFeedbackSetting.data;
      } else if (typeof predefiniedRatingFeedbackSetting.data === 'object') {
        // If it's an object, convert to array
        feedbackArray = Object.values(predefiniedRatingFeedbackSetting.data);
      }

      if (feedbackArray.length > 0) {
        const feedbackOptions = feedbackArray.map((item, index) => ({
          id: item.id || `feedback_${index}`,
          text: item.text || '',
          isActive: item.isActive !== false,
          order: item.order || (index + 1)
        }));
        const sortedOptions = feedbackOptions.sort((a, b) => a.order - b.order);
        console.log('✅ Processed feedback options:', sortedOptions);
        setData(sortedOptions);
      } else {
        console.log('📭 No feedback data found, showing empty table');
        setData([]);
      }
    } else {
      console.log('📭 No feedback data in Redux state, showing empty table');
      setData([]);
    }
  }, [predefiniedRatingFeedbackSetting?.data]);

  // Fetch data from Firebase on component mount
  useEffect(() => {
    fetchDataFromFirebase();
  }, [fetchDataFromFirebase]);

  const handleRowAdd = useCallback((newData) => {
    return new Promise((resolve, reject) => {
      try {
        // Validation
        if (!newData.text || !newData.text.trim()) {
          setSnackbar({
            open: true,
            message: 'Feedback text is required',
            severity: 'error'
          });
          reject(new Error('Feedback text is required'));
          return;
        }

        // Check if trying to activate when already 7 active items
        const activeCount = data.filter(item => item.isActive).length;
        if (newData.isActive && activeCount >= 7) {
          setSnackbar({
            open: true,
            message: 'Maximum 7 feedback options can be active. Please deactivate another option first.',
            severity: 'error'
          });
          reject(new Error('Maximum 7 active options allowed'));
          return;
        }

        // Create new item
        const newItem = {
          id: `feedback_${Date.now()}`,
          text: newData.text.trim(),
          isActive: false, // Default to false for new items
          order: newData.order !== undefined ? parseInt(newData.order) : data.length + 1
        };

        // Update local state and sort by order
        const updatedData = [...data, newItem].sort((a, b) => a.order - b.order);
        setData(updatedData);

        // Save to Firebase
        saveToFirebase(updatedData);

        setSnackbar({
          open: true,
          message: 'Feedback option added successfully!',
          severity: 'success'
        });

        resolve();
      } catch (error) {
        console.error('Error adding row:', error);
        setSnackbar({
          open: true,
          message: 'Error adding feedback option. Please try again.',
          severity: 'error'
        });
        reject(error);
      }
    });
  }, [data]);

  const handleRowUpdate = useCallback((newData, oldData) => {
    return new Promise((resolve, reject) => {
      try {
        // Validation
        if (!newData.text || !newData.text.trim()) {
          setSnackbar({
            open: true,
            message: 'Feedback text is required',
            severity: 'error'
          });
          reject(new Error('Feedback text is required'));
          return;
        }

        // Check if trying to activate when already 7 active items (excluding current item)
        const activeCount = data.filter(item => item.isActive && item.id !== oldData.id).length;
        if (newData.isActive && activeCount >= 7) {
          setSnackbar({
            open: true,
            message: 'Maximum 7 feedback options can be active. Please deactivate another option first.',
            severity: 'error'
          });
          reject(new Error('Maximum 7 active options allowed'));
          return;
        }

        // Find and update the item
        const updatedData = data.map(item => {
          if (item.id === oldData.id) {
            return {
              ...item,
              text: newData.text.trim(),
              isActive: newData.isActive !== false,
              order: newData.order !== undefined ? parseInt(newData.order) : item.order
            };
          }
          return item;
        });

        // Update local state and sort by order
        const sortedData = updatedData.sort((a, b) => a.order - b.order);
        setData(sortedData);

        // Save to Firebase
        saveToFirebase(sortedData);

        setSnackbar({
          open: true,
          message: 'Feedback option updated successfully!',
          severity: 'success'
        });

        resolve();
      } catch (error) {
        console.error('Error updating row:', error);
        setSnackbar({
          open: true,
          message: 'Error updating feedback option. Please try again.',
          severity: 'error'
        });
        reject(error);
      }
    });
  }, [data]);

  const handleRowDelete = useCallback((oldData) => {
    return new Promise((resolve, reject) => {
      try {
        const updatedData = data.filter(item => item.id !== oldData.id);
        setData(updatedData);
        saveToFirebase(updatedData);

        setSnackbar({
          open: true,
          message: 'Feedback option deleted successfully!',
          severity: 'success'
        });

        resolve();
      } catch (error) {
        console.error('Error deleting row:', error);
        setSnackbar({
          open: true,
          message: 'Error deleting feedback option. Please try again.',
          severity: 'error'
        });
        reject(error);
      }
    });
  }, [data]);

  const saveToFirebase = useCallback(async (updatedData) => {
    try {
      setIsSaving(true);
      const { firebase } = require('common/src/config/configureFirebase');
      const { set, ref } = require("firebase/database");

      console.log('💾 Saving feedback data to Firebase:', updatedData);

      // Convert array to object with IDs as keys for better Firebase structure
      const feedbackObject = {};
      updatedData.forEach(feedback => {
        feedbackObject[feedback.id] = {
          ...feedback,
          updatedAt: new Date().toISOString()
        };
      });

      // Save directly to the new path using set instead of update
      const feedbackRef = ref(firebase.database, 'predefiniedRatingFeedbackSetting');
      await set(feedbackRef, feedbackObject);

      console.log('✅ Feedback data saved successfully to Firebase');

      setSnackbar({
        open: true,
        message: t('feedback_saved_successfully') || 'Feedback options saved successfully!',
        severity: 'success'
      });
    } catch (error) {
      console.error('❌ Error saving feedback options:', error);
      setSnackbar({
        open: true,
        message: t('error_saving_feedback') || 'Error saving feedback options. Please try again.',
        severity: 'error'
      });
    } finally {
      setIsSaving(false);
    }
  }, [t]);

  const columns = useMemo(() => [
    {
      title: t('feedback_tags'),
      field: 'text',
      editComponent: props => (
        <TextField
          value={props.value || ''}
          onChange={(e) => props.onChange(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              e.stopPropagation();
            }
          }}
          fullWidth
          variant="outlined"
          placeholder="Enter feedback text..."
          size="small"
        />
      ),
      cellStyle: {
        paddingLeft: '16px',
        paddingRight: '16px'
      }
    },
    {
      title: t('active'),
      field: 'isActive',
      type: 'boolean',
      editComponent: props => {
        const activeCount = data.filter(item => item.isActive).length;
        const isDisabled = !props.value && activeCount >= 7;

        return (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '8px' }}>
            <input
              type="checkbox"
              checked={props.value === true}
              onChange={(e) => {
                e.preventDefault();
                e.stopPropagation();
                props.onChange(e.target.checked);
              }}
              disabled={isDisabled}
              style={{
                transform: 'scale(1.2)',
                opacity: isDisabled ? 0.5 : 1,
                cursor: isDisabled ? 'not-allowed' : 'pointer'
              }}
              title={isDisabled ? 'Maximum 7 active options reached' : ''}
            />
          </div>
        );
      },
      render: rowData => (
        <div style={{ display: 'flex', justifyContent: 'center' }}>
          <span style={{
            color: rowData.isActive ? '#4caf50' : '#f44336',
            fontWeight: 'bold',
            fontSize: '14px'
          }}>
            {rowData.isActive ? '✓' : '✗'}
          </span>
        </div>
      ),
      cellStyle: {
        textAlign: 'center',
        paddingLeft: '16px',
        paddingRight: '16px'
      },
      headerStyle: {
        textAlign: 'center'
      }
    },
    {
      title: t('display_order'),
      field: 'order',
      type: 'numeric',
      editComponent: props => (
        <TextField
          value={props.value || 1}
          onChange={(e) => {
            const value = parseInt(e.target.value) || 1;
            props.onChange(Math.max(1, value));
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              e.stopPropagation();
            }
          }}
          variant="outlined"
          size="small"
          style={{ width: '80px' }}
        />
      ),
      render: rowData => (
        <span style={{
          backgroundColor: '#f5f5f5',
          padding: '4px 8px',
          borderRadius: '4px',
          fontSize: '12px',
          fontWeight: 'bold'
        }}>
          {rowData.order}
        </span>
      ),
      cellStyle: {
        textAlign: 'center',
        paddingLeft: '16px',
        paddingRight: '16px'
      },
      headerStyle: {
        textAlign: 'center'
      }
    }
  ], [t, data]);

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Header Section */}
      <div style={{
        padding: '20px 24px 16px 24px',
        borderTopLeftRadius: '8px',
        borderTopRightRadius: '8px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between'
      }}>
        <Typography
          component="h1"
          variant="h6"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            fontWeight: '600',
            color: '#333'
          }}
        >
          <SettingsIcon style={{ color: '#1976d2' }} />
          {t('feedback_tag_text') || 'Predefined Feedback Settings'}
          <span style={{ marginLeft: '12px', fontSize: '14px', fontWeight: 'normal' }}>
            ({data.filter(item => item.isActive).length}/7 active)
          </span>
          {isSaving && (
            <span style={{ marginLeft: '12px', fontSize: '14px', color: '#e0e0e0' }}>
              💾 Saving...
            </span>
          )}
        </Typography>
      </div>

      {/* Table Container */}
      <div style={{
        flex: 1,
        backgroundColor: colors.WHITE,
        borderBottomLeftRadius: '8px',
        borderBottomRightRadius: '8px',
        padding: '0',
        overflow: 'hidden'
      }}>
        <MaterialTable
          title=""
          columns={columns}
          data={data}
          key={`feedback-table-${data.length}`}
          options={{
            search: true,
            searchFieldAlignment: 'right',
            searchAutoFocus: true,
            searchFieldVariant: 'standard',
            filtering: false,
            paging: true,
            pageSize: 10,
            showTitle: false,
            headerStyle: {
              backgroundColor: colors.MAIN_COLOR,
              color: colors.WHITE,
              fontWeight: 'bold',
              paddingLeft: '16px',
              paddingRight: '16px'
            },
            rowStyle: {
              fontSize: '14px',
              paddingLeft: '16px',
              paddingRight: '16px'
            },
            actionsColumnIndex: -1,
            exportButton: false,
            showTitle: false,
            sorting: true,
            toolbar: true,
            toolbarButtonAlignment: 'right',
            loadingType: 'linear',
            emptyRowsWhenPaging: false,
            draggable: false,
            addRowPosition: 'first',
            emptyDataSourceMessage: t('blank_message') || 'No Data',
            reload: false,
            refresh: false,
            thirdSortClick: false,
            debounceInterval: 300
          }}
          editable={{
            onRowAdd: handleRowAdd,
            onRowUpdate: handleRowUpdate,
            onRowDelete: handleRowDelete,
            isEditable: () => true,
            isDeletable: () => true,
            onRowAddCancelled: () => { },
            onRowUpdateCancelled: () => { },
            onRowDeleteCancelled: () => { },
          }}
          onBulkUpdate={() => { }}
          onBulkDelete={() => { }}
          onSelectionChange={() => { }}
          onSearchChange={() => { }}
          onOrderChange={() => { }}
          onPageChange={() => { }}
          onRowClick={() => { }}
          onRowDoubleClick={() => { }}
          onFilterChange={() => { }}
          onColumnResize={() => { }}
          onTreeExpandChange={() => { }}
          onTreeExpand={() => { }}
          onQueryChange={() => { }}
          onQueryChangeDebounce={() => { }}
          onRefresh={() => { }}
          onRefreshDebounce={() => { }}
          onRowDataChange={() => { }}
          onRowDataChangeDebounce={() => { }}
          localization={{
            body: {
              editRow: {
                deleteText: 'Are you sure you want to delete this feedback option?',
                cancelTooltip: t('cancel'),
                saveTooltip: t('save')
              },
              editTooltip: t('edit'),
              deleteTooltip: t('delete'),
              addTooltip: t('add')
            },
            toolbar: {
              searchTooltip: t('search'),
              searchPlaceholder: t('search'),
              addRemoveColumns: 'Add or remove columns',
              nRowsSelected: '{0} row(s) selected',
              showColumnsTitle: 'Show Columns',
              showColumnsAriaLabel: 'Show Columns'
            },
            pagination: {
              labelRowsSelect: t('rows'),
              labelDisplayedRows: `{from}-{to} ${t('of') || 'of'} {count}`,
              firstTooltip: t('first_page') || 'First Page',
              previousTooltip: t('previous_page') || 'Previous Page',
              nextTooltip: t('next_page') || 'Next Page',
              lastTooltip: t('last_page') || 'Last Page'
            },
            body: {
              emptyDataSourceMessage: t('blank_message') || 'No Data'
            }
          }}
        />
      </div>

      {/* Success/Error Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </div>
  );
});

export default memo(PredefinedFeedbackSettings);
